import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Paper, 
    Typography, 
    List, 
    ListItem, 
    ListItemText,
    CircularProgress,
    Box,
    Chip,
    TextField,
    InputAdornment,
    IconButton,
    Alert,
    useTheme,
    useMediaQuery,
    Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import authApi from '../services/authApi';
import { sharePointService } from '../services/sharePointService';
import { useUserSession } from '../contexts/UserContext';
import { useSharePoint } from '../contexts/SharePointContext';
import NavigationDrawer from './Navigation/NavigationDrawer';
import { useNavigate } from 'react-router-dom';

function ETLMonitoring() {
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const { userSession } = useUserSession();
    const isSystemAdmin = (userSession?.roles || []).includes('System Admin');
    const { effectiveConfigId } = useSharePoint();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const navigate = useNavigate();

    // Function to format the timestamp (now just returns the DB timestamp)
    const formatTimestamp = (timestamp) => {
        return timestamp;
    };

    // Function to get log level color
    const getLogLevelColor = (level) => {
        switch (level.toLowerCase()) {
            case 'error':
                return 'error';
            case 'warning':
                return 'warning';
            case 'info':
                return 'primary';
            case 'success':
                return 'success';
            default:
                return 'default';
        }
    };

    // Filter logs based on search term
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredLogs(logs);
            return;
        }

        const filtered = logs.filter(log => 
            log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
            log.log_level.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredLogs(filtered);
    }, [logs, searchTerm]);

    // Fetch logs and set up WebSocket
    useEffect(() => {
        const fetchLogs = async () => {
            try {
                if (!userSession?.email) {
                    console.log('No user session available');
                    setError('User session not available.');
                    setLoading(false);
                    return;
                }

                setLoading(true);
                setError(null);
                const token = authApi.getToken();
                
                // Fetch logs using user email
                console.log('Fetching ETL logs for user:', userSession.email);
                const response = await sharePointService.getETLLogs(token, userSession.email);
                console.log('ETL logs received:', response);
                setLogs(response);
            } catch (error) {
                console.error('Error fetching ETL logs:', error);
                console.error('Error details:', error.response || error);
                const errorMessage = error.response?.data?.message || error.message || 'Failed to load logs';
                setError(errorMessage);
                setLogs([]);
            } finally {
                setLoading(false);
            }
        };

        if (userSession?.effectiveEmail || userSession?.email) {
            fetchLogs();

            // Set up WebSocket connection after getting config
            let ws = null;
            const setupWebSocket = async () => {
                try {
                    const token = authApi.getToken();
                    const config = await sharePointService.getConfig(token);
                    
                    // Handle case where config might not be found initially
                    if (!config || config.success === false || !config.configs || config.configs.length === 0) {
                        console.log('No SharePoint configuration found for WebSocket');
                        setError('No SharePoint configuration found for this user to establish real-time updates.');
                        return;
                    }
                    
                    const configId = effectiveConfigId || config.configs[0]?.id;

                    // Determine WebSocket URL based on environment
                    let wsUrl;
                    if (process.env.REACT_APP_WS_URL) {
                        // If REACT_APP_WS_URL is set, use it directly with /ws path
                        const baseUrl = process.env.REACT_APP_WS_URL.endsWith('/') 
                            ? process.env.REACT_APP_WS_URL.slice(0, -1) 
                            : process.env.REACT_APP_WS_URL;
                        wsUrl = `${baseUrl}/ws?configId=${configId}`;
                    } else {
                        // Otherwise, construct the URL based on current location
                        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
                        const wsHost = window.location.hostname === 'localhost' ? 'localhost:5000' : window.location.host;
                        wsUrl = `${wsProtocol}//${wsHost}/ws?configId=${configId}`;
                    }
                    
                    console.log('Connecting to WebSocket:', wsUrl);
                    ws = new WebSocket(wsUrl);
                    
                    ws.onmessage = (event) => {
                        const newLog = JSON.parse(event.data);
                        setLogs(prevLogs => [newLog, ...prevLogs]);
                    };

                    ws.onopen = () => {
                        console.log('WebSocket connected successfully');
                        setError(null); // Clear any previous connection errors
                    };

                    ws.onerror = (error) => {
                        console.error('WebSocket error:', error);
                        setError('Failed to establish real-time connection for logs.');
                    };

                    ws.onclose = (event) => {
                        console.log('WebSocket connection closed:', event.code, event.reason);
                        if (event.code !== 1000) { // Not a normal closure
                            setError('Real-time connection lost. Logs will not update automatically.');
                        }
                    };
                } catch (error) {
                    console.error('WebSocket setup error:', error);
                    setError('Error setting up real-time log updates.');
                }
            };

            setupWebSocket();

            // Cleanup function
            return () => {
                if (ws && ws.readyState === WebSocket.OPEN) {
                    ws.close();
                }
            };
        } else {
            setLoading(false);
            setError('User session not available.');
        }
    }, [userSession, effectiveConfigId]);

    const handleSearchClear = () => {
        setSearchTerm('');
    };

    const renderLogEntry = (log, index) => {
        // Create a unique key combining multiple properties
        const uniqueKey = `${log.created_at}-${log.log_level}-${index}`;
        
        // Format the timestamp to show EST time directly
        const timestamp = new Date(log.created_at).toLocaleString('en-US', {
            timeZone: 'America/New_York',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        }) + ' EST';

        return (
            <ListItem 
                key={uniqueKey}
                sx={{ 
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    alignItems: 'flex-start'
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="textSecondary">
                            {timestamp}
                        </Typography>
                        <Chip
                            label={log.log_level}
                            size="small"
                            color={getLogLevelColor(log.log_level)}
                            sx={{ ml: 1 }}
                        />
                    </Box>
                    <Typography>{log.message}</Typography>
                </Box>
            </ListItem>
        );
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
                <NavigationDrawer
                    isMobile={isMobile}
                    isDrawerOpen={isDrawerOpen}
                    setIsDrawerOpen={setIsDrawerOpen}
                    activeSection={''}
                    setActiveSection={() => {}}
                    navigate={navigate}
                />
                <Box component="main" sx={{ flexGrow: 1, p: 3, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
            <NavigationDrawer
                isMobile={isMobile}
                isDrawerOpen={isDrawerOpen}
                setIsDrawerOpen={setIsDrawerOpen}
                activeSection={''}
                setActiveSection={() => {}}
                navigate={navigate}
            />
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                        <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                            ETL Process Monitor
                        </Typography>
                        {isSystemAdmin && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => navigate('/admin/etl-operations')}
                            >
                                ETL Operations
                            </Button>
                        )}
                    </Box>
                    
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Search Box */}
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search logs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleSearchClear} edge="end">
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Box>

                    {/* Results count */}
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        Showing {filteredLogs.length} of {logs.length} logs
                    </Typography>

                    <List>
                        {filteredLogs.map((log, index) => renderLogEntry(log, index))}
                    </List>
                </Paper>
            </Box>
        </Box>
    );
}

export default ETLMonitoring; 